import axios from "axios";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { retryAsync } from "ts-retry";

import { useAuthStore } from "@hd/auth/auth.store";
import { debugLog, errorLog } from "@hd/logger/logger";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";

import { HubContext, HubContextCallbacks } from "./hub-context";
import { HubStatus } from "./hub-status";
import { useHubStore } from "./hub.store";

interface HubConnectionData {
  connection: HubConnection;
  subscribersCount: number;
  status: HubStatus;
}

function getKey(companyId: number, userId?: string): string {
  return userId ? `${companyId}-${userId}` : companyId.toString();
}

async function connectionIsReady(connectionData: HubConnectionData) {
  if (connectionData.status === HubStatus.Connected) return;
  await new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (connectionData.status === HubStatus.Connected) {
        clearInterval(interval);
        resolve();
      }
    }, 200);
  });
}

function HubProvider({ children }: PropsWithChildren) {
  const username = useAuthStore((state) => state.username) as string;
  const connections = useRef<Map<string, HubConnectionData>>(new Map());

  const setHubStatus = useHubStore((s) => s.setHubStatus);

  const setConnectionStatus = useCallback(
    (
      status: HubStatus,
      connectionData: HubConnectionData | undefined,
      key: string
    ) => {
      if (connectionData) {
        connectionData.status = status;
      }
      // se una connessione è solo per companyId (quindi non è relativa al lock del preventivo/richieste) imposto l'hub status.
      if (!Number.isNaN(Number(key))) {
        setHubStatus(Number(key), status);
      }
    },
    [setHubStatus]
  );

  const setUpConnection = useCallback(
    async (companyId: number, customUserId?: string) => {
      const connectionKey = getKey(companyId, customUserId);
      let connectionData = connections.current.get(connectionKey);
      if (!connectionData) {
        const hubName = `c${companyId}`;
        const userId = customUserId || `${companyId}~${username}~${1}`;
        const connection = new HubConnectionBuilder()
          .withUrl(
            import.meta.env.VITE_HD_CHAT_OPERATOR_URL +
              `/api?userId=${userId}&hub=${hubName}`
          )
          .withAutomaticReconnect()
          .configureLogging(LogLevel[import.meta.env.VITE_HD_LOG_LEVEL])
          .build();

        connectionData = {
          connection,
          subscribersCount: 0,
          status: HubStatus.Connecting,
        };

        connections.current.set(connectionKey, connectionData);

        setConnectionStatus(
          HubStatus.Connecting,
          connectionData,
          connectionKey
        );

        async function openConnection(maxTry: number = 1) {
          try {
            await retryAsync(
              async () => {
                await connection.start();
                await axios.post(
                  `${
                    import.meta.env.VITE_HD_CHAT_OPERATOR_URL
                  }/api/operators/join?userId=${userId}&hub=${hubName}`
                );
              },
              {
                delay: (params) => {
                  const delay = 1000 * Math.pow(params.currentTry, 2);
                  return delay;
                },
                maxTry,
              }
            );

            debugLog("Opened connection for companyId", companyId);
            setConnectionStatus(
              HubStatus.Connected,
              connectionData,
              connectionKey
            );
          } catch (error) {
            errorLog("Error opening connection", error);
            throw error;
          }
        }

        await openConnection(5);

        connectionData.connection.onreconnected(() => {
          debugLog("Reconnected connection for companyId", companyId);
          setConnectionStatus(
            HubStatus.Connected,
            connectionData,
            connectionKey
          );
        });

        connectionData.connection.onreconnecting(() => {
          debugLog("Reconnecting connection for companyId", companyId);
          setConnectionStatus(
            HubStatus.Connecting,
            connectionData,
            connectionKey
          );
        });

        connectionData.connection.onclose(async () => {
          debugLog("Closed connection for companyId", companyId);

          // if (
          //   connections.current.has(connectionKey) &&
          //   connectionData?.status !== HubStatus.Connecting
          // ) {
          //   setConnectionStatus(
          //     HubStatus.Connecting,
          //     connectionData,
          //     connectionKey
          //   );
          //   debugLog("Trying to reconnect connection for companyId", companyId);
          //   try {
          //     await openConnection(1);
          //   } catch (e) {
          //     errorLog("Error opening connection", e);
          //   }
          // }

          setConnectionStatus(
            HubStatus.Disconnected,
            connectionData,
            connectionKey
          );
        });
      } else if (connectionData.status !== HubStatus.Connected) {
        await connectionIsReady(connectionData);
      }

      return connectionData;
    },
    [username, setConnectionStatus]
  );

  const subscribe = useCallback(
    async (
      companyId: number,
      hubCallbacks: HubContextCallbacks,
      customUserId?: string
    ) => {
      const connectionData = await setUpConnection(companyId, customUserId);

      const connection = connectionData.connection;
      connectionData.subscribersCount++;

      if (hubCallbacks.onNewMessage) {
        connection.on("NewMessage", hubCallbacks.onNewMessage);
      }

      if (hubCallbacks.onMessagesRead) {
        connection.on("ReadMessages", hubCallbacks.onMessagesRead);
      }

      if (hubCallbacks.onPersonStatusUpdated) {
        connection.on(
          "PersonStatusUpdated",
          hubCallbacks.onPersonStatusUpdated
        );
      }

      if (hubCallbacks.onQuotationLocked) {
        connection.on("QuotationLocked", hubCallbacks.onQuotationLocked);
      }

      if (hubCallbacks.onQuotationUnlocked) {
        connection.on("QuotationUnlocked", hubCallbacks.onQuotationUnlocked);
      }

      if (hubCallbacks.onRequestLocked) {
        connection.on("RequestLocked", hubCallbacks.onRequestLocked);
      }

      if (hubCallbacks.onRequestUnlocked) {
        connection.on("RequestUnlocked", hubCallbacks.onRequestUnlocked);
      }
    },
    [setUpConnection]
  );

  const unsubscribe = useCallback(
    async (
      companyId: number,
      hubCallbacks: HubContextCallbacks,
      customUserId?: string
    ) => {
      const connectionKey = getKey(companyId, customUserId);
      const connectionData = connections.current.get(connectionKey);
      if (connectionData) {
        await connectionIsReady(connectionData);

        connectionData.subscribersCount--;
        const connection = connectionData.connection;

        if (hubCallbacks.onNewMessage) {
          connection.off("NewMessage", hubCallbacks.onNewMessage);
        }

        if (hubCallbacks.onMessagesRead) {
          connection.off("ReadMessages", hubCallbacks.onMessagesRead);
        }

        if (hubCallbacks.onPersonStatusUpdated) {
          connection.off(
            "PersonStatusUpdated",
            hubCallbacks.onPersonStatusUpdated
          );
        }

        if (hubCallbacks.onQuotationLocked) {
          connection.off("QuotationLocked", hubCallbacks.onQuotationLocked);
        }

        if (hubCallbacks.onQuotationUnlocked) {
          connection.off("QuotationUnlocked", hubCallbacks.onQuotationUnlocked);
        }

        if (hubCallbacks.onRequestLocked) {
          connection.off("RequestLocked", hubCallbacks.onRequestLocked);
        }

        if (hubCallbacks.onRequestUnlocked) {
          connection.off("RequestUnlocked", hubCallbacks.onRequestUnlocked);
        }

        if (connectionData.subscribersCount === 0) {
          connections.current.delete(connectionKey);
          await connection.stop();
        }
      }
    },
    []
  );

  const value = useMemo(() => {
    return { subscribe, unsubscribe };
  }, [subscribe, unsubscribe]);

  // Cleanup connections whenever the component unmounts
  useEffect(() => {
    const currentConnections = connections.current;
    return () => {
      currentConnections.forEach(async (data) => {
        if (data.connection.state === "Connected") {
          await data.connection?.stop();
        }
      });
    };
  }, []);

  return <HubContext.Provider value={value} children={children} />;
}

export default HubProvider;
